import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import { BuiltLogoMark } from '../ui/BuiltLogoMark';

const HERO_IMAGE = '/images/brand/hero-workshop-document.jpg';

interface HomeHeroProps {
  variant?: 'public' | 'authenticated';
  greeting?: string | null;
  subtitle?: string | null;
}

export const HomeHero: React.FC<HomeHeroProps> = ({
  variant = 'public',
  greeting,
  subtitle,
}) => {
  const navigate = useNavigate();

  const trackCta = (cta: string) => {
    void apiService.trackEvent('home_cta_clicked', { metadata: { cta, surface: 'hero' } });
  };

  if (variant === 'authenticated') {
    return (
      <header className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(249,115,22,0.12),transparent_55%)]" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <p className="kicker mb-3">Bilt</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white text-balance">
            {greeting || 'Welcome back'}
          </h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl leading-relaxed mt-2">
            {subtitle || 'Your garage. Your builds. Their stories.'}
          </p>
        </div>
      </header>
    );
  }

  return (
    <header className="relative min-h-[100dvh] overflow-hidden">
      <img
        src={HERO_IMAGE}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[center_35%] scale-[1.02]"
        fetchPriority="high"
        decoding="async"
      />
      {/* Warm charcoal scrims — keep brand orange for accents only */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/88 to-[#0a0a0a]/35"
        aria-hidden
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent"
        aria-hidden
      />
      <div className="film-grain z-[1]" aria-hidden />

      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end min-h-[100dvh] pb-20 sm:pb-28 pt-32">
        <div className="max-w-[38rem]">
          <div className="flex items-center gap-3 mb-10 motion-safe:animate-fade-in">
            <BuiltLogoMark size="md" />
            <span className="font-display text-2xl tracking-tight text-white">Bilt</span>
            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-300/90 bg-orange-500/10 border border-orange-500/25">
              Beta
            </span>
          </div>

          <p className="kicker mb-5 motion-safe:animate-fade-in">Build documentation</p>

          <h1 className="font-display text-[2.85rem] leading-[0.98] sm:text-6xl md:text-[4.5rem] font-semibold tracking-[-0.03em] text-white mb-6 text-balance motion-safe:animate-fade-in">
            Every build
            <br className="hidden sm:block" />{' '}
            <span className="italic font-medium text-white/95 leading-[1.1] pb-1 inline-block">
              has a story.
            </span>
          </h1>

          <p className="text-gray-300/95 text-base sm:text-lg leading-relaxed mb-10 max-w-[34ch] motion-safe:animate-fade-in">
            Document your modifications, media, events, awards, and milestones in one place.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
            <Button
              variant="primary"
              size="lg"
              className="sm:px-9 shadow-brand"
              onClick={() => {
                trackCta('create_garage');
                navigate('/register');
              }}
            >
              Start your garage
            </Button>
            <button
              type="button"
              onClick={() => {
                trackCta('discover_builds');
                navigate('/discover');
              }}
              className="group inline-flex items-center justify-center gap-2 min-h-[48px] px-1 text-sm font-medium text-gray-200 hover:text-white transition-colors duration-200 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
            >
              Browse builds
              <span
                aria-hidden
                className="transition-transform duration-200 group-hover:translate-x-0.5 text-orange-400"
              >
                &rarr;
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HomeHero;
